import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Landmark, History } from "lucide-react";

const inputCls = "w-full bg-pb-elevated border border-pb-border px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400";
const lbl = "block text-[10px] font-bold uppercase tracking-widest text-zinc-500 mb-1.5";
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const PlatformFeePanel = () => {
  const [fees, setFees] = useState(null);
  const [summary, setSummary] = useState([]);
  const [audit, setAudit] = useState([]);
  const [busy, setBusy] = useState(false);
  const [example, setExample] = useState(50);

  const load = () => {
    api.get("/admin/service-fees").then(({ data }) => setFees(data)).catch(() => {});
    api.get("/admin/service-fees/platform-summary").then(({ data }) => setSummary(data || [])).catch(() => {});
    api.get("/admin/service-fees/audit").then(({ data }) => setAudit(data || [])).catch(() => {});
  };
  useEffect(load, []);

  if (!fees) return <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;

  const pct = Number(fees.platform_fee_pct) || 0;
  const flat = Number(fees.platform_fee_flat) || 0;
  const on = fees.platform_fee_enabled !== false;
  const svc = Number(example) * (Number(fees.service_charge_pct) || 0) / 100 + (Number(fees.service_fee_flat) || 0);
  const plat = on ? Number(example) * pct / 100 + flat : 0;

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/service-fees", {
        service_charge_pct: fees.service_charge_pct, service_fee_flat: fees.service_fee_flat,
        platform_fee_pct: pct, platform_fee_flat: flat, platform_fee_enabled: on,
      });
      setFees(data);
      toast.success(on ? "Platform fee saved — applied to every new tournament & league card checkout" : "Platform fee turned off");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="platform-fee-panel">
      <p className="text-xs text-zinc-500">
        A <span className="text-zinc-300">platform fee</span> added on top of the service fee, but only on <span className="text-amber-300">tournament & league Stripe transactions</span>
        {" "}(entries, second events, partner-invite entries and the card half of split payments). Memberships, store, coaching and coin purchases are untouched. Coin (BLC) payments are never charged.
      </p>

      <div className="grid sm:grid-cols-[auto_1fr_1fr_auto] gap-3 items-end">
        <label className="flex items-center gap-2 cursor-pointer border border-pb-border bg-pb-elevated px-3 py-2 h-[38px]" data-testid="platform-fee-toggle">
          <input type="checkbox" checked={on} onChange={(e) => setFees({ ...fees, platform_fee_enabled: e.target.checked })} className="accent-amber-400 w-4 h-4" data-testid="platform-fee-enabled" />
          <span className={`text-[10px] font-bold uppercase tracking-widest ${on ? "text-amber-300" : "text-zinc-500"}`}>{on ? "Active" : "Off"}</span>
        </label>
        <div>
          <label className={lbl}>Platform Fee (%)</label>
          <input data-testid="platform-fee-pct" type="number" min={0} max={30} step={0.1} value={fees.platform_fee_pct}
            onChange={(e) => setFees({ ...fees, platform_fee_pct: e.target.value })} className={inputCls} />
        </div>
        <div>
          <label className={lbl}>Platform Fee (flat $)</label>
          <input data-testid="platform-fee-flat" type="number" min={0} max={100} step={0.25} value={fees.platform_fee_flat}
            onChange={(e) => setFees({ ...fees, platform_fee_flat: e.target.value })} className={inputCls} />
        </div>
        <button data-testid="platform-fee-save" onClick={save} disabled={busy}
          className="bg-amber-400 text-black font-black uppercase tracking-widest px-5 py-2.5 text-[10px] hover:bg-amber-300 active:scale-95 transition-all disabled:opacity-40">
          {busy ? "Saving…" : "Save Platform Fee"}
        </button>
      </div>

      <div className="border border-pb-border bg-pb-elevated/40 px-3 py-2.5 text-[11px]" data-testid="platform-fee-example">
        <div className="flex flex-wrap items-center gap-2 mb-1.5">
          <span className="text-zinc-500 uppercase tracking-widest text-[10px]">Preview an entry fee of</span>
          <span className="text-zinc-400">$</span>
          <input data-testid="platform-fee-example-input" type="number" min={0} step={5} value={example} onChange={(e) => setExample(e.target.value)}
            className="w-20 bg-pb-surface border border-pb-border px-2 py-1 text-xs text-white focus:outline-none focus:border-amber-400" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 font-mono">
          <div><div className="text-zinc-600 text-[9px] uppercase tracking-widest">Entry</div><div className="text-zinc-300">{money(example)}</div></div>
          <div><div className="text-zinc-600 text-[9px] uppercase tracking-widest">Service fee</div><div className="text-zinc-300">{money(svc)}</div></div>
          <div><div className="text-zinc-600 text-[9px] uppercase tracking-widest">Platform fee</div><div className={on ? "text-amber-300" : "text-zinc-600"} data-testid="platform-fee-example-platform">{money(plat)}</div></div>
          <div><div className="text-zinc-600 text-[9px] uppercase tracking-widest">Player pays</div><div className="text-[#A3E635] font-bold" data-testid="platform-fee-example-total">{money(Number(example) + svc + plat)}</div></div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-pb-border p-3" data-testid="platform-fee-summary">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-2"><Landmark className="w-3.5 h-3.5" /> Collected (paid Stripe transactions)</div>
          {summary.length === 0 ? <p className="text-xs text-zinc-600">No platform fees collected yet.</p> : (
            <div className="space-y-1 text-xs">
              {summary.map((r) => (
                <div key={r.kind} className="flex justify-between gap-2" data-testid={`platform-fee-summary-${r.kind}`}>
                  <span className="text-zinc-300">{r.kind === "tournament_entry" ? "Tournament entries" : r.kind === "league_entry" ? "League entries" : r.kind.replace("_", " ")} <span className="text-zinc-600">× {r.count}</span></span>
                  <span className="font-mono text-amber-300">{money(r.platform_fee)}</span>
                </div>
              ))}
              <div className="flex justify-between border-t border-pb-border pt-1 mt-1 font-bold">
                <span className="text-zinc-400">Total platform fees</span>
                <span className="font-mono text-amber-300" data-testid="platform-fee-summary-total">{money(summary.reduce((n, r) => n + r.platform_fee, 0))}</span>
              </div>
            </div>
          )}
        </div>
        <div className="border border-pb-border p-3" data-testid="fee-audit">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-2"><History className="w-3.5 h-3.5" /> Fee change log</div>
          {audit.length === 0 ? <p className="text-xs text-zinc-600">No changes recorded yet.</p> : (
            <div className="space-y-1.5 max-h-40 overflow-y-auto text-[11px]">
              {audit.map((a, i) => (
                <div key={i} className="border-b border-pb-border/50 pb-1" data-testid={`fee-audit-row-${i}`}>
                  <span className="text-zinc-500">{new Date(a.at).toLocaleString()} · {a.by_name || "admin"}</span>
                  <div className="text-zinc-300 font-mono">
                    svc {a.before.service_charge_pct}% + ${a.before.service_fee_flat} → {a.after.service_charge_pct}% + ${a.after.service_fee_flat}
                    {" · "}plat {a.before.platform_fee_enabled === false ? "off" : `${a.before.platform_fee_pct}% + $${a.before.platform_fee_flat}`} → {a.after.platform_fee_enabled === false ? "off" : `${a.after.platform_fee_pct}% + $${a.after.platform_fee_flat}`}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlatformFeePanel;
